/**
 * Pure availability math — no DB access.
 * The caller supplies the studio's opening window, booking-policy
 * constraints and real booked ranges read from the shared database.
 */

export interface Slot {
  /** minutes from midnight, Asia/Seoul */
  startMinute: number;
  available: boolean;
}

export interface DayWindow {
  openMinute: number;
  closeMinute: number;
}

/** Bookable slots are offered on the hour */
const SLOT_STEP_MINUTES = 60;

/** Minimum advance booking days (reservations are allowed from 3 days after today) */
export const MIN_BOOKING_ADVANCE_DAYS = 3;

/** Every reservation occupies a fixed 2-hour window */
export const SESSION_MINUTES = 120;

/** Fallback studio hours when no StudioSchedule rows exist */
export const DEFAULT_DAY_WINDOW: DayWindow = {
  openMinute: 10 * 60,
  closeMinute: 20 * 60,
};

export interface GetDaySlotsInput {
  window: DayWindow;
  /** duration + bufferBefore + bufferAfter */
  blockMinutes: number;
  /** current minute-of-day in Seoul when the date is today */
  nowMinuteOfDay?: number;
  minAdvanceMinutes: number;
  bookedRanges: Array<{ start: number; end: number }>;
}

export function getDaySlots({
  window,
  blockMinutes,
  nowMinuteOfDay,
  minAdvanceMinutes,
  bookedRanges,
}: GetDaySlotsInput): Slot[] {
  const isToday = nowMinuteOfDay !== undefined;
  const earliest = isToday ? nowMinuteOfDay! + minAdvanceMinutes : -Infinity;

  const slots: Slot[] = [];
  for (
    let start = window.openMinute;
    start + blockMinutes <= window.closeMinute;
    start += SLOT_STEP_MINUTES
  ) {
    const reservedEnd = start + blockMinutes;

    const overlaps = bookedRanges.some(
      (r) => start < r.end && reservedEnd > r.start,
    );
    const tooSoon = start < earliest;

    slots.push({ startMinute: start, available: !overlaps && !tooSoon });
  }
  return slots;
}

export function minutesToLabel(minute: number): string {
  const h = Math.floor(minute / 60);
  const m = minute % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Seoul has no DST — fixed +09:00 offset */
export function seoulDateToUtcIso(
  dateISO: string,
  minuteOfDay: number,
): string {
  const [y, m, d] = dateISO.split("-").map(Number);
  const utcMs = Date.UTC(y, m - 1, d, 0, minuteOfDay) - 9 * 60 * 60 * 1000;
  return new Date(utcMs).toISOString();
}

export function utcIsoToSeoulDateAndMinute(iso: string): {
  dateISO: string;
  minuteOfDay: number;
} {
  const ms = new Date(iso).getTime() + 9 * 60 * 60 * 1000;
  const d = new Date(ms);
  return {
    dateISO: d.toISOString().slice(0, 10),
    minuteOfDay: d.getUTCHours() * 60 + d.getUTCMinutes(),
  };
}

/** Today in Seoul as YYYY-MM-DD and current minute-of-day */
export function nowInSeoul(): { dateISO: string; minuteOfDay: number } {
  return utcIsoToSeoulDateAndMinute(new Date().toISOString());
}

export function addDaysISO(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** 0 = Sunday … 6 = Saturday, matching Prisma's DayOfWeek ordering needs */
export function weekdayIndexOf(dateISO: string): number {
  const [y, m, d] = dateISO.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}
